import { openDB } from "idb";

export interface Favorite { itemId: string; savedAt: string; }
export interface ReadingPosition { documentId: string; anchorId: string; updatedAt: string; }
export interface ImportPreview { favorites: Favorite[]; readingPositions: ReadingPosition[]; summary: { favorites: number; readingPositions: number } }
let dbPromise: ReturnType<typeof openDB> | undefined;
const database = () => dbPromise ??= openDB("life-map-user-state", 1, {
  upgrade(db) {
    db.createObjectStore("favorites", { keyPath: "itemId" });
    db.createObjectStore("positions", { keyPath: "documentId" });
  }
});
export const listFavorites = () => database().then((db) => db.getAll("favorites") as Promise<Favorite[]>);
export const isFavorite = (id: string) => database().then((db) => db.get("favorites", id)).then(Boolean);
export async function setFavorite(id: string, value: boolean) {
  const db = await database();
  if (value) await db.put("favorites", { itemId: id, savedAt: new Date().toISOString() });
  else await db.delete("favorites", id);
}
export async function savePosition(documentId: string, anchorId: string) {
  const db = await database(); await db.put("positions", { documentId, anchorId, updatedAt: new Date().toISOString() });
}
export const listPositions = () => database().then((db) => db.getAll("positions") as Promise<ReadingPosition[]>);
export async function clearState() { const db = await database(); await Promise.all([db.clear("favorites"), db.clear("positions")]); }
export async function exportState(sourceCommit: string) {
  return { schemaVersion: 1, exportedAt: new Date().toISOString(), sourceCommit, favorites: await listFavorites(), readingPositions: await listPositions(), preferences: {} };
}
const dangerous = new Set(["__proto__", "constructor", "prototype"]);
function assertSafeObject(value: unknown, label: string): asserts value is Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value) || Object.getPrototypeOf(value) !== Object.prototype) throw new Error(`${label}必须是普通 JSON 对象`);
  for (const key of Object.keys(value)) if (dangerous.has(key)) throw new Error(`${label}包含危险键 ${key}`);
}
function validDate(value: unknown, label: string) {
  if (typeof value !== "string" || !Number.isFinite(Date.parse(value))) throw new Error(`${label}不是有效时间`);
  if (Date.parse(value) > Date.now() + 5 * 60_000) throw new Error(`${label}不能来自未来`);
  return value;
}
export function validateImportState(raw: unknown): ImportPreview {
  assertSafeObject(raw, "备份");
  const serialized = JSON.stringify(raw); if (new TextEncoder().encode(serialized).byteLength > 5 * 1024 * 1024) throw new Error("备份超过 5 MiB");
  const allowed = new Set(["schemaVersion", "exportedAt", "sourceCommit", "favorites", "readingPositions", "preferences"]);
  for (const key of Object.keys(raw)) if (!allowed.has(key)) throw new Error(`备份包含未知字段 ${key}`);
  if (raw.schemaVersion !== 1 || !Array.isArray(raw.favorites) || !Array.isArray(raw.readingPositions)) throw new Error("备份版本或字段无效");
  if (raw.favorites.length > 5000 || raw.readingPositions.length > 1000) throw new Error("备份记录数量超过上限");
  if (raw.exportedAt !== undefined) validDate(raw.exportedAt, "导出时间");
  if (raw.sourceCommit !== undefined && (typeof raw.sourceCommit !== "string" || !/^[0-9a-f]{40}$/.test(raw.sourceCommit))) throw new Error("来源版本无效");
  if (raw.preferences !== undefined) assertSafeObject(raw.preferences, "偏好设置");
  const favoriteIds = new Set<string>();
  const favorites = raw.favorites.map((record, index) => {
    assertSafeObject(record, `收藏 ${index + 1}`); if (!Object.keys(record).every((key) => ["itemId", "savedAt"].includes(key))) throw new Error(`收藏 ${index + 1} 包含未知字段`);
    if (typeof record.itemId !== "string" || !/^LM\d{6}$/.test(record.itemId)) throw new Error(`收藏 ${index + 1} 的条目 ID 无效`);
    if (favoriteIds.has(record.itemId)) throw new Error(`收藏中存在重复条目 ${record.itemId}`); favoriteIds.add(record.itemId);
    return { itemId: record.itemId, savedAt: validDate(record.savedAt, `收藏 ${index + 1} 时间`) };
  });
  const positionIds = new Set<string>();
  const readingPositions = raw.readingPositions.map((record, index) => {
    assertSafeObject(record, `阅读位置 ${index + 1}`); if (!Object.keys(record).every((key) => ["documentId", "anchorId", "updatedAt"].includes(key))) throw new Error(`阅读位置 ${index + 1} 包含未知字段`);
    if (typeof record.documentId !== "string" || !/^[a-z0-9-]+$/.test(record.documentId) || typeof record.anchorId !== "string" || !/^(?:top|LM\d{6}|[a-z0-9_-]+)$/i.test(record.anchorId)) throw new Error(`阅读位置 ${index + 1} 的标识无效`);
    if (positionIds.has(record.documentId)) throw new Error(`阅读位置中存在重复文档 ${record.documentId}`); positionIds.add(record.documentId);
    return { documentId: record.documentId, anchorId: record.anchorId, updatedAt: validDate(record.updatedAt, `阅读位置 ${index + 1} 时间`) };
  });
  return { favorites, readingPositions, summary: { favorites: favorites.length, readingPositions: readingPositions.length } };
}
export async function importState(raw: unknown) {
  const value = validateImportState(raw);
  const db = await database(); const tx = db.transaction(["favorites", "positions"], "readwrite");
  try {
    for (const favorite of value.favorites) await tx.objectStore("favorites").put(favorite);
    for (const position of value.readingPositions) await tx.objectStore("positions").put(position);
    await tx.done;
  } catch (error) { tx.abort(); throw error; }
  return value.summary;
}
